import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Stack,
  InputAdornment,
  Tooltip,
  useMediaQuery,
  useTheme,
  Collapse,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Business as BusinessIcon,
  LocalShipping as TruckIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
  LocationOn,
  Phone,
} from "@mui/icons-material";
import { freightsByParty, partyList } from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";
import { Autocomplete } from "@mui/material";
import { useDebounce } from "../../../Hooks/useDebounce";
import { getLorryReceiptBlobUrl } from "../Freight/pdfGenerator";
import { pdfGenerator } from "../Freight/pdfGenerator";
import { T } from "../../../theme";

const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryBg,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};
const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};
const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};
const iconBtn = { ...ghostBtn, minWidth: 0, px: 1 };

const filterInputSx = {
  "& .MuiInputBase-root": { height: 34, ...sq, bgcolor: T.primaryBg },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
  "& .MuiAutocomplete-inputRoot": { height: 34, ...sq, bgcolor: T.primaryBg },
};

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const formatCurrency = (a) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(a || 0);

// ── StatCard ───────────────────────────────────────────────────────
const StatCard = ({ title, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1, sm: 1.5 },
      textAlign: "center",
      ...sq,
      border: `1.5px solid ${color}40`,
      borderTop: `3px solid ${color}`,
      bgcolor: `${color}08`,
      boxShadow: "none",
      minWidth: 0,
    }}
  >
    <Typography
      sx={{
        fontSize: { xs: "0.55rem", sm: "0.65rem" },
        color: T.textSub,
        lineHeight: 1.2,
        mb: 0.3,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.03em",
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: { xs: "0.72rem", sm: "1rem" },
        fontWeight: 700,
        color,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "block",
      }}
      title={value}
    >
      {value}
    </Typography>
  </Paper>
);

// ── InfoRow helper ─────────────────────────────────────────────────
const LabelValue = ({ label, value, color }) => (
  <Box>
    <Typography
      sx={{
        fontSize: "0.58rem",
        color: T.textMuted,
        fontWeight: 600,
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: "0.7rem", fontWeight: 600, color: color || T.text }}
    >
      {value}
    </Typography>
  </Box>
);

// ── MobileTruckCard ────────────────────────────────────────────────
const MobileTruckCard = ({ row, idx, expanded, onToggle, onPreview }) => (
  <Paper
    elevation={0}
    sx={{
      mb: 1,
      ...sq,
      border: `1.5px solid ${expanded ? T.primary : T.border}`,
      bgcolor: T.white,
      overflow: "hidden",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
    }}
  >
    {/* Header Row */}
    <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
      {/* Icon */}
      <Box
        sx={{
          width: 58,
          flexShrink: 0,
          bgcolor: expanded ? T.primary : T.primaryBg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
          transition: "background-color 0.2s",
          gap: 0.25,
        }}
      >
        <TruckIcon
          sx={{ fontSize: 20, color: expanded ? T.white : T.primary }}
        />
        <Typography
          sx={{
            fontSize: "0.46rem",
            fontWeight: 700,
            color: expanded ? T.white : T.primary,
          }}
        >
          #{idx + 1}
        </Typography>
      </Box>

      {/* Main info */}
      <Box
        sx={{
          flex: 1,
          px: 1.2,
          py: 0.75,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.82rem",
            color: T.text,
            lineHeight: 1.2,
            mb: 0.15,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.truck_no || "—"}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.62rem",
            color: T.textSub,
            fontWeight: 500,
            mb: 0.15,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.party_name || "—"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <LocationOn sx={{ fontSize: 10, color: T.textMuted }} />
          <Typography
            sx={{
              fontSize: "0.58rem",
              color: T.textMuted,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.from_location || "—"} → {row.to_location || "—"}
          </Typography>
        </Box>
      </Box>

      {/* Amount + toggle */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "flex-end",
          px: 1,
          py: 0.75,
          minWidth: 100,
        }}
      >
        <Box sx={{ textAlign: "right" }}>
          <Typography
            sx={{ fontSize: "0.48rem", color: T.textMuted, fontWeight: 600 }}
          >
            Net Payable
          </Typography>
          <Typography
            sx={{ fontWeight: 700, fontSize: "0.8rem", color: T.red }}
          >
            ₹{row.net_payable || 0}
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={onToggle}
          startIcon={
            expanded ? (
              <VisibilityOff sx={{ fontSize: 12 }} />
            ) : (
              <Visibility sx={{ fontSize: 12 }} />
            )
          }
          sx={{
            ...ghostBtn,
            height: 22,
            fontSize: "0.55rem",
            px: 0.7,
            "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
          }}
        >
          {expanded ? "Hide" : "View"}
        </Button>
      </Box>
    </Box>

    {/* Expanded detail */}
    {expanded && (
      <Box
        sx={{
          bgcolor: T.primaryBg,
          borderTop: `1.5px solid ${T.border}`,
          p: 1.25,
        }}
      >
        <Stack spacing={0.75}>
          {/* Basic Info */}
          <Grid container spacing={0.5}>
            {[
              ["ID", row.freight_id],
              ["Loading", formatDate(row.loading_date)],
              ["Unloading", formatDate(row.unloading_date)],
            ].map(([l, v]) => (
              <Grid item xs={6} key={l}>
                <LabelValue label={l} value={v} />
              </Grid>
            ))}
          </Grid>

          {/* Weight */}
          <Box
            sx={{
              p: 0.75,
              bgcolor: T.white,
              border: `1px solid ${T.border}`,
              ...sq,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.62rem",
                fontWeight: 700,
                color: T.primary,
                mb: 0.4,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              ⚖️ Weight
            </Typography>
            <Grid container spacing={0.5}>
              {[
                ["Load", row.loading_wt || 0, null],
                ["Recv", row.received_wt || 0, null],
                ["Short", row.shortage_wt || 0, T.red],
                ["Bill", row.billing_wt || 0, null],
              ].map(([l, v, c]) => (
                <Grid item xs={3} key={l}>
                  <LabelValue label={l} value={v} color={c} />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Charges */}
          <Box
            sx={{
              p: 0.75,
              bgcolor: T.white,
              border: `1px solid ${T.border}`,
              ...sq,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.62rem",
                fontWeight: 700,
                color: T.primary,
                mb: 0.4,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              💰 Charges
            </Typography>
            <Grid container spacing={0.5}>
              {[
                ["Rate", `₹${row.freight_rate || 0}`],
                ["LR Amt", `₹${row.freight_amt || 0}`],
                ["TDS", `₹${row.tds_amt || 0}`],
                ["Shortage", `₹${row.shortage_amt || 0}`],
                ["Commission", `₹${row.commission || 0}`],
                ["Other Exp", `₹${row.other_exp || 0}`],
              ].map(([l, v]) => (
                <Grid item xs={6} key={l}>
                  <LabelValue label={l} value={v} />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Advance */}
          <Box
            sx={{
              p: 0.75,
              bgcolor: T.warningBg,
              border: `1px solid ${T.warning}30`,
              ...sq,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.62rem",
                fontWeight: 700,
                color: T.warning,
                mb: 0.4,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              💵 Advance
            </Typography>
            <Grid container spacing={0.5}>
              {[
                ["Mines", `₹${row.ad_mines_exp || 0}`],
                ["Diesel", `₹${row.ad_diesel_exp || 0}`],
                ["Cash", `₹${row.ad_cash_exp || 0}`],
              ].map(([l, v]) => (
                <Grid item xs={4} key={l}>
                  <LabelValue label={l} value={v} color={T.warning} />
                </Grid>
              ))}
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: T.warning,
                  }}
                >
                  Total: ₹{row.total_advance || 0}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Owner */}
          {(row.truck_owner || row.phone_no || row.pan_no) && (
            <Box
              sx={{
                p: 0.75,
                bgcolor: T.white,
                border: `1px solid ${T.border}`,
                ...sq,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.4,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                👤 Owner
              </Typography>
              {row.truck_owner && (
                <LabelValue label="Owner" value={row.truck_owner} />
              )}
              {row.phone_no && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 0.25,
                  }}
                >
                  <Phone sx={{ fontSize: 11, color: T.primary }} />
                  <Typography sx={{ fontSize: "0.7rem" }}>
                    {row.phone_no}
                  </Typography>
                </Box>
              )}
              {row.pan_no && <LabelValue label="PAN" value={row.pan_no} />}
            </Box>
          )}

          {/* Summary */}
          <Box
            sx={{
              p: 0.75,
              bgcolor: T.redBg,
              border: `1.5px solid ${T.redBorder}`,
              ...sq,
            }}
          >
            <Grid container spacing={0.5}>
              {[
                ["Net Amount", `₹${row.net_amount || 0}`, null],
                ["Net Payable", `₹${row.net_payable || 0}`, T.primary],
                ["Paid", `₹${row.paid_amount?.toFixed(1) || 0}`, T.success],
                ["Balance", `₹${row.bal_amount?.toFixed(1) || 0}`, T.red],
                ["Received", `₹${row.receive_amount?.toFixed(1) || 0}`, null],
                [
                  "Difference",
                  `₹${row.difference_amount?.toFixed(1) || 0}`,
                  null,
                ],
              ].map(([l, v, c]) => (
                <Grid item xs={6} key={l}>
                  <LabelValue label={l} value={v} color={c} />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Remarks */}
          {row.remark && (
            <Box>
              <Typography
                sx={{
                  fontSize: "0.58rem",
                  color: T.textMuted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                Remark
              </Typography>
              <Typography
                sx={{ fontSize: "0.7rem", color: T.text, fontWeight: 500 }}
              >
                {row.remark}
              </Typography>
            </Box>
          )}
          {row.payment_remark && (
            <Box>
              <Typography
                sx={{
                  fontSize: "0.58rem",
                  color: T.textMuted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                Payment Remark
              </Typography>
              <Typography
                sx={{ fontSize: "0.7rem", color: T.text, fontWeight: 500 }}
              >
                {row.payment_remark}
              </Typography>
            </Box>
          )}
        </Stack>

        {/* ── Actions ── */}
        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            mt: 1.25,
            pt: 1.25,
            borderTop: `1px dashed ${T.border}`,
            justifyContent: "flex-end",
          }}
        >
          <Button
            size="small"
            startIcon={<Visibility sx={{ fontSize: 13 }} />}
            onClick={() => onPreview(row)}
            sx={{ ...ghostBtn, height: 30, px: 1.5 }}
          >
            Preview
          </Button>
          <Button
            size="small"
            startIcon={<PrintIcon sx={{ fontSize: 13 }} />}
            onClick={() => pdfGenerator(row)}
            sx={{ ...ghostBtn, height: 30, px: 1.5 }}
          >
            Print
          </Button>
        </Box>
      </Box>
    )}
  </Paper>
);

// ── Table Header Config ────────────────────────────────────────────
const TH = [
  { l: "#", w: 35, sticky: true, left: 0 },
  { l: "LR No", w: 80, sticky: true, left: 35 },
  { l: "Truck No", w: 85, sticky: true, left: 115 },
  { l: "Party", w: 120 },
  { l: "Loading Date", w: 85 },
  { l: "Loading Out", w: 100 },
  { l: "Unloading Date", w: 100 },
  { l: "Unloading Out", w: 120 },
  { l: "From", w: 90 },
  { l: "To", w: 90 },
  { l: "E-way Bill No", w: 100 },
  { l: "E-way Bill Date", w: 100 },
  { l: "E-way Validity", w: 130 },
  { l: "Invoice No", w: 100 },
  { l: "Classification", w: 130 },
  { l: "Packing Method", w: 130 },
  { l: "Truck Owner", w: 110 },
  { l: "Phone", w: 95 },
  { l: "Ref No", w: 90 },
  { l: "Remark", w: 140 },
  { l: "Additional Info", w: 160 },
  { l: "Date", w: 90 },
  { l: "Actions", w: 65, sticky: true, right: 0 },
];
const COLSPAN = TH.length;

// ── Main Component ─────────────────────────────────────────────────
export default function Partyfreight() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [data, setData] = useState([]);
  const [partyListdata, setPartyList] = useState([]);
  const [selectedParty, setSelectedParty] = useState(null);
  const [lrFilter, setLrFilter] = useState("");
  const [truckFilter, setTruckFilter] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [expandedCardId, setExpandedCardId] = useState(null);

  const debouncedLrFilter = useDebounce(lrFilter, 500);
  const debouncedTruckFilter = useDebounce(truckFilter, 500);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const handlePreviewOpen = async (row) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const url = await getLorryReceiptBlobUrl(row);
      setPreviewUrl(url);
    } catch (err) {
      console.error("Error generating preview:", err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewClose = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewOpen(false);
  };

  const handlePrintSingleRow = (row) => pdfGenerator(row);
  const handleCardToggle = (id) =>
    setExpandedCardId((p) => (p === id ? null : id));

  // ADD
  useEffect(() => {
    const fetchParties = async () => {
      try {
        const res = await partyList({ party_type: "consignor" });
        setPartyList(Array.isArray(res) ? res : []);
      } catch (error) {
        console.error("Failed to fetch parties:", error);
        setPartyList([]);
      }
    };
    fetchParties();
  }, []);

  const fetchFreights = useCallback(
    async (partyId, pageNo = 1, append = false, overrideFilters = null) => {
      if (!partyId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const filters = overrideFilters ?? {
          lr_number: debouncedLrFilter || undefined,
          truck_no: debouncedTruckFilter || undefined,
        };
        const res = await freightsByParty(partyId, pageNo, filters);
        const results = res?.results || [];
        setData((prev) => (append ? [...prev, ...results] : results));
        setHasMore(!!res?.next);
        setPage(pageNo + 1);
      } catch {
        setData([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [debouncedLrFilter, debouncedTruckFilter],
  );

  useEffect(() => {
    if (!selectedParty) return;
    fetchFreights(selectedParty, 1, false);
    setExpandedCardId(null);
  }, [debouncedLrFilter, debouncedTruckFilter, selectedParty, fetchFreights]);

  const fetchMoreData = () => {
    if (!selectedParty || !hasMore || loading) return;
    fetchFreights(selectedParty, page, true);
  };

  const handlePartyChange = (partyId) => {
    setSelectedParty(partyId);
    setData([]);
    setPage(1);
    setHasMore(true);
    setExpandedCardId(null);
  };

  const handleSearch = () => {
    if (!selectedParty) return;
    setData([]);
    setPage(1);
    setHasMore(true);
    fetchFreights(selectedParty, 1, false);
    setExpandedCardId(null);
  };

  const clearFilters = () => {
    setLrFilter("");
    setTruckFilter("");
    if (selectedParty) {
      setData([]);
      setPage(1);
      setHasMore(true);
      setTimeout(
        () =>
          fetchFreights(selectedParty, 1, false, {
            lr_number: undefined,
            truck_no: undefined,
          }),
        0,
      );
    }
  };

  const truckOptions = [
    ...new Set(data.map((i) => i.truck_no).filter(Boolean)),
  ];

  // ── Export Excel ──────────────────────────────────────────────
  const handleExportExcel = async () => {
    const XLSX = await import("xlsx-js-style");

    const headers = [
      "Sr No",
      "LR ID",
      "Party Name",
      "Loading Date",
      "Unloading Date",
      "Truck No.",
      "D.O. No.",
      "T.P. No.",
      "From",
      "To",
      "Loading Wt",
      "Receiving Wt",
      "Shortage Wt",
      "Billing Wt",
      "LR Rate",
      "LR Amt",
      "TDS %",
      "TDS Amt",
      "Shortage Rate",
      "Shortage Amt",
      "Ad Mines",
      "Ad Diesel",
      "Ad Cash",
      "Total Ad",
      "Commission",
      "Net Amt",
      "Others Exp.",
      "Net Payable",
      "Paid Amt",
      "Bal Amt",
      "Truck Owner",
      "Phone No.",
      "PAN No.",
      "Remark",
      "Rec Amt",
      "Diff Amt",
      "Payment Remark",
      "Date",
    ];

    const rows = data.map((row, i) => [
      i + 1,
      row.freight_id ?? "—",
      row.party_name || "—",
      formatDate(row.loading_date),
      formatDate(row.unloading_date),
      row.truck_no || "—",
      row.do_no || "—",
      row.tp_no || "—",
      row.from_location || "—",
      row.to_location || "—",
      row.loading_wt ?? "—",
      row.received_wt ?? "—",
      row.shortage_wt ?? "—",
      row.billing_wt ?? "—",
      row.freight_rate ?? "—",
      row.freight_amt ?? "—",
      row.tds_per ?? "—",
      row.tds_amt ?? "—",
      row.shortage_rate ?? "—",
      row.shortage_amt ?? "—",
      row.ad_mines_exp ?? "—",
      row.ad_diesel_exp ?? "—",
      row.ad_cash_exp ?? "—",
      row.total_advance ?? "—",
      row.commission ?? "—",
      row.net_amount ?? "—",
      row.other_exp ?? "—",
      row.net_payable ?? "—",
      row.paid_amount ?? "—",
      row.bal_amount ?? "—",
      row.truck_owner || "—",
      row.phone_no || "—",
      row.pan_no || "—",
      row.remark || "—",
      row.receive_amount ?? "—",
      row.difference_amount ?? "—",
      row.payment_remark || "—",
      formatDate(row.created_at),
    ]);

    // ── Style Definitions ──────────────────────────────────────────
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10, name: "Segoe UI" },
      fill: { fgColor: { rgb: "4F46E5" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "3730A3" } },
        bottom: { style: "thin", color: { rgb: "3730A3" } },
        left: { style: "thin", color: { rgb: "3730A3" } },
        right: { style: "thin", color: { rgb: "3730A3" } },
      },
    };

    const cellStyleEven = {
      font: { sz: 10, name: "Segoe UI", color: { rgb: "1E1B4B" } },
      fill: { fgColor: { rgb: "F5F3FF" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "E0E7FF" } },
        bottom: { style: "thin", color: { rgb: "E0E7FF" } },
        left: { style: "thin", color: { rgb: "E0E7FF" } },
        right: { style: "thin", color: { rgb: "E0E7FF" } },
      },
    };

    const cellStyleOdd = {
      ...cellStyleEven,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };

    const numberStyle = {
      ...cellStyleEven,
      font: { ...cellStyleEven.font, bold: true, color: { rgb: "4F46E5" } },
    };

    const numberStyleOdd = {
      ...numberStyle,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };

    // ── Build Worksheet Data ───────────────────────────────────────
    const wsData = [
      headers.map((h) => ({ v: h, t: "s", s: headerStyle })),
      ...rows.map((row, rowIdx) =>
        row.map((val, colIdx) => {
          const isEven = rowIdx % 2 === 0;
          const isNumber = colIdx === 0;

          let style;
          if (isNumber) {
            style = isEven ? numberStyle : numberStyleOdd;
          } else {
            style = isEven ? cellStyleEven : cellStyleOdd;
          }

          return {
            v: val ?? "—",
            t: typeof val === "number" ? "n" : "s",
            s: style,
          };
        }),
      ),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // ── Column Widths (no wrapping, width based on longest content) ──
    const colWidths = headers.map((_, colIdx) => {
      const allValues = [
        headers[colIdx],
        ...rows.map((r) => String(r[colIdx] ?? "")),
      ];
      const maxLen = Math.max(...allValues.map((v) => v.length));
      return { wch: maxLen + 4 };
    });

    ws["!cols"] = colWidths;

    // ── Row Heights ────────────────────────────────────────────────
    ws["!rows"] = [{ hpt: 18 }];

    // ── Freeze Header Row ──────────────────────────────────────────
    ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2" };

    const wb = XLSX.utils.book_new();
    wb.Props = {
      Title: "Party LR",
      Author: "Staff Management System",
      CreatedDate: new Date(),
    };

    XLSX.utils.book_append_sheet(wb, ws, "Party LR");
    XLSX.writeFile(
      wb,
      `Party_Freights_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  const handlePrint = () => {
    const partyName =
      partyListdata.find((p) => p.party_id === selectedParty)?.name || "";
    const win = window.open("", "_blank");
    win.document.write(`<html><head><title>Party LR</title>
      <style>body{font-family:Arial;padding:20px}h2,h4{text-align:center}
      table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px;text-align:left}
      th{background:#f5f5f5}</style></head>
      <body><h2>PARAS ROAD LINES</h2><h4>Party LR - ${partyName}</h4>
      <table><thead><tr><th>S.No</th><th>LR ID</th><th>Loading Date</th>
      <th>Truck No.</th><th>From</th><th>To</th><th>Net Payable</th></tr></thead>
      <tbody>${data
        .map(
          (row, i) => `<tr><td>${i + 1}</td><td>${row.freight_id || "—"}</td>
      <td>${formatDate(row.loading_date)}</td><td>${row.truck_no || "—"}</td>
      <td>${row.from_location || "—"}</td><td>${row.to_location || "—"}</td>
      <td>₹${row.net_payable || 0}</td></tr>`,
        )
        .join("")}
      </tbody></table></body></html>`);
    win.document.close();
    win.print();
    win.close();
  };

  // ── Empty state ───────────────────────────────────────────────
  const EmptyState = ({ message }) => (
    <Box
      sx={{
        textAlign: "center",
        py: 8,
        border: `1.5px dashed ${T.border}`,
        bgcolor: T.primaryBg,
        ...sq,
      }}
    >
      <BusinessIcon sx={{ fontSize: 44, color: T.border, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: T.text, fontSize: "0.9rem" }}>
        {message}
      </Typography>
    </Box>
  );

  // ─────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -2, maxWidth: 1400, mx: "auto", width: "100%" }}
    >
      {/* ── Toolbar ── */}
      <Box sx={{ mb: 1.5 }}>
        <Grid container spacing={0.75} alignItems="center">
          {/* TP Filter */}
          <Grid item xs={6} sm={3} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="LR No."
              value={lrFilter}
              onChange={(e) => setLrFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ReceiptIcon sx={{ fontSize: 13, color: T.textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>
          {/* Party Autocomplete */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={partyListdata}
              getOptionLabel={(o) => o?.name || ""}
              value={
                partyListdata.find((p) => p.party_id === selectedParty) || null
              }
              onChange={(_, v) => handlePartyChange(v?.party_id || null)}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Party"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <BusinessIcon
                            sx={{ fontSize: 14, color: T.primary }}
                          />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={filterInputSx}
                />
              )}
            />
          </Grid>

          {/* Truck Filter */}
          <Grid item xs={6} sm={3} md={3}>
            <Autocomplete
              freeSolo
              options={truckOptions}
              value={truckFilter}
              onInputChange={(_, v) => setTruckFilter(v || "")}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Truck No."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <TruckIcon
                            sx={{ fontSize: 13, color: T.textMuted }}
                          />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={filterInputSx}
                />
              )}
            />
          </Grid>

          {/* Search */}
          <Grid item xs="auto">
            <Tooltip title="Search" arrow>
              <Button
                size="small"
                onClick={handleSearch}
                disabled={loading}
                sx={{ ...primaryBtn, height: 34, minWidth: 34, px: 1 }}
              >
                {loading ? (
                  <CircularProgress size={14} sx={{ color: T.white }} />
                ) : (
                  <SearchIcon sx={{ fontSize: 15 }} />
                )}
              </Button>
            </Tooltip>
          </Grid>

          {/* Spacer */}
          <Grid
            item
            xs={false}
            md
            sx={{ display: { xs: "none", md: "block" } }}
          />

          {/* Clear */}
          <Grid item xs="auto">
            <Tooltip title="Clear Filters" arrow>
              <span>
                <Button
                  size="small"
                  onClick={clearFilters}
                  sx={{ ...dangerBtn, height: 34, minWidth: 34, px: 1 }}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Button>
              </span>
            </Tooltip>
          </Grid>

          {/* Export */}
          <Grid item xs="auto">
            <Tooltip title="Export Excel" arrow>
              <Button
                size="small"
                onClick={handleExportExcel}
                sx={{ ...iconBtn, height: 34, minWidth: 34, px: 1 }}
              >
                <DownloadIcon sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid>

          {/* Print */}
          <Grid item xs="auto">
            <Tooltip title="Print" arrow>
              <Button
                size="small"
                onClick={handlePrint}
                sx={{ ...iconBtn, height: 34, minWidth: 34, px: 1 }}
              >
                <PrintIcon sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* ── Mobile View ── */}
      {isMobile ? (
        <Box>
          {loading && data.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 10,
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: T.primary }} size={32} />
              <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
                Loading…
              </Typography>
            </Box>
          ) : !selectedParty ? (
            <EmptyState message="Please select a party to view records" />
          ) : data.length === 0 ? (
            <EmptyState message="No records found" />
          ) : (
            <Box
              id="party-mobile"
              sx={{ maxHeight: "calc(100vh - 320px)", overflow: "auto" }}
            >
              <InfiniteScroll
                dataLength={data.length}
                next={fetchMoreData}
                hasMore={hasMore && !!selectedParty}
                loader={
                  <Box sx={{ textAlign: "center", py: 1 }}>
                    <CircularProgress size={20} sx={{ color: T.primary }} />
                  </Box>
                }
                scrollableTarget="party-mobile"
              >
                {data.map((row, idx) => (
                  <MobileTruckCard
                    key={row.freight_id || idx}
                    row={row}
                    idx={idx}
                    expanded={expandedCardId === (row.freight_id || idx)}
                    onToggle={() => handleCardToggle(row.freight_id || idx)}
                    onPreview={handlePreviewOpen}
                  />
                ))}
              </InfiniteScroll>
            </Box>
          )}
        </Box>
      ) : (
        /* ── Desktop View ── */
        <>
          {/* Main LR Table */}
          <Paper
            elevation={0}
            sx={{
              ...sq,
              border: `1.5px solid ${T.border}`,
              overflow: "hidden",
              boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
              height: "calc(100vh - 150px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TableContainer
              id="scrollableDiv"
              sx={{ flex: 1, minHeight: 0, overflow: "auto" }}
            >
              <InfiniteScroll
                dataLength={data.length}
                next={fetchMoreData}
                hasMore={hasMore && !!selectedParty}
                loader={
                  <Box sx={{ textAlign: "center", py: 1 }}>
                    <CircularProgress size={20} sx={{ color: T.primary }} />
                  </Box>
                }
                scrollableTarget="scrollableDiv"
                style={{ overflow: "visible" }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: 2800, // ← updated
                    borderCollapse: "collapse",
                    "& td, & th": {
                      py: 0,
                      px: 0.75,
                      fontSize: "0.75rem",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                    },
                    "& td": {
                      borderBottom: `1px solid #ccc`,
                      height: 26,
                    },
                    "& th": {
                      fontWeight: 700,
                      color: T.headerText,
                      bgcolor: `${T.headerBg} !important`,
                      py: 0.2,
                      height: 28,
                      fontSize: "0.8rem",
                      letterSpacing: "0.01em",
                      borderBottom: `1px solid #ccc`,
                      lineHeight: 1.2,
                    },
                  }}
                >
                  {/* ── TableHead — driven by TH array, no change needed ── */}
                  <TableHead>
                    <TableRow>
                      {TH.map(({ l, w, sticky, left, right }, i) => (
                        <TableCell
                          key={l || i}
                          sx={{
                            minWidth: w,
                            bgcolor: `${T.headerBg} !important`,
                            color: `${T.headerText} !important`,
                            ...(sticky && {
                              position: "sticky !important",
                              zIndex: "60 !important",
                              ...(left !== undefined && {
                                left: `${left}px !important`,
                              }),
                              ...(right !== undefined && {
                                right: `${right}px !important`,
                              }),
                            }),
                          }}
                        >
                          {l}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  {/* ── TableBody ── */}
                  <TableBody>
                    {!selectedParty ? (
                      <TableRow>
                        <TableCell
                          colSpan={COLSPAN}
                          sx={{ textAlign: "center", py: 6, border: "none" }}
                        >
                          <BusinessIcon
                            sx={{
                              fontSize: 40,
                              color: T.border,
                              mb: 1,
                              display: "block",
                              mx: "auto",
                            }}
                          />
                          <Typography
                            sx={{
                              color: T.textSub,
                              fontSize: "0.8rem",
                              fontWeight: 600,
                            }}
                          >
                            Please select a party to view records
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : loading && data.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={COLSPAN}
                          sx={{ textAlign: "center", py: 6, border: "none" }}
                        >
                          <CircularProgress
                            sx={{ color: T.primary }}
                            size={32}
                          />
                        </TableCell>
                      </TableRow>
                    ) : data.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={COLSPAN}
                          sx={{ textAlign: "center", py: 6, border: "none" }}
                        >
                          <ReceiptIcon
                            sx={{
                              fontSize: 40,
                              color: T.border,
                              mb: 1,
                              display: "block",
                              mx: "auto",
                            }}
                          />
                          <Typography
                            sx={{
                              color: T.textSub,
                              fontSize: "0.8rem",
                              fontWeight: 600,
                            }}
                          >
                            No records found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.map((row, idx) => {
                        const isEven = idx % 2 === 0;
                        const rowBg = isEven ? T.baseRow : T.altRow;
                        const sc = {
                          bgcolor: `${rowBg} !important`,
                          zIndex: 5,
                        };

                        return (
                          <TableRow
                            key={row.freight_id || idx}
                            sx={{
                              "& > td": {
                                bgcolor: rowBg,
                                transition: "background-color 0.12s",
                              },
                              "&:hover > td": {
                                bgcolor: `${T.rowHover} !important`,
                              },
                            }}
                          >
                            <TableCell
                              sx={{ position: "sticky", left: 0, ...sc }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 700,
                                  color: T.textMuted,
                                }}
                              >
                                {idx + 1}
                              </Typography>
                            </TableCell>

                            {/* ── LR No ── sticky left 35 */}
                            <TableCell
                              sx={{ position: "sticky", left: 35, ...sc }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: T.text,
                                }}
                              >
                                {row.lr_number || "—"}
                              </Typography>
                            </TableCell>

                            {/* ── Truck No ── sticky left 115 */}
                            <TableCell
                              sx={{ position: "sticky", left: 115, ...sc }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: T.primary,
                                  fontSize: "0.75rem",
                                }}
                              >
                                {row.truck_no || "—"}
                              </Typography>
                            </TableCell>

                            {/* ── Party ── */}
                            <TableCell>
                              <Tooltip title={row.party_name || ""} arrow>
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    maxWidth: 120,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.party_name || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {formatDate(row.loading_date)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {formatDate(row.loading_out_date)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {formatDate(row.unloading_date)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {formatDate(row.unloading_out_date)}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Tooltip title={row.from_location || ""} arrow>
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    maxWidth: 90,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.from_location || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={row.to_location || ""} arrow>
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    maxWidth: 90,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.to_location || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {row.eway_bill_no || "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {formatDate(row.eway_bill_date)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {formatDate(row.eway_validity_date)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {row.invoice_no || "—"}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Tooltip
                                title={row.classification_of_goods || ""}
                                arrow
                              >
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    maxWidth: 120,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.classification_of_goods || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Tooltip
                                title={row.method_of_packing || ""}
                                arrow
                              >
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    maxWidth: 120,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.method_of_packing || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={row.truck_owner || ""} arrow>
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    maxWidth: 100,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.truck_owner || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {row.phone_no || "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.text }}
                              >
                                {row.tp_no || "—"}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Tooltip title={row.remark || ""} arrow>
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    maxWidth: 130,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.remark || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={row.additional_info || ""} arrow>
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    maxWidth: 150,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.additional_info || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={formatDate(row.created_at)}
                                size="small"
                                sx={{
                                  fontSize: "0.6rem",
                                  height: 16,
                                  ...sq,
                                  bgcolor: T.primaryBg,
                                  color: T.primary,
                                  border: `1px solid ${T.border}`,
                                  fontWeight: 700,
                                  "& .MuiChip-label": { px: 0.75 },
                                }}
                              />
                            </TableCell>

                            {/* ── Actions ── sticky right */}
                            <TableCell
                              sx={{
                                position: "sticky",
                                right: 0,
                                ...sc,
                                py: "2px !important",
                              }}
                            >
                              <Stack
                                direction="row"
                                spacing={0.5}
                                justifyContent="center"
                              >
                                <Tooltip
                                  title="Preview LR"
                                  arrow
                                  placement="top"
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => handlePreviewOpen(row)}
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      ...sq,
                                      color: "#1976d2",
                                      bgcolor: "#e3f2fd",
                                      border: `1px solid #bbdefb`,
                                      transition: "all 0.18s",
                                      "&:hover": {
                                        bgcolor: "#bbdefb",
                                        borderColor: "#1976d2",
                                        transform: "translateY(-1px)",
                                      },
                                    }}
                                  >
                                    <Visibility sx={{ fontSize: 12 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Print" arrow placement="top">
                                  <IconButton
                                    size="small"
                                    onClick={() => handlePrintSingleRow(row)}
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      ...sq,
                                      color: T.success,
                                      bgcolor: T.successBg,
                                      border: `1px solid ${T.successBorder}`,
                                      transition: "all 0.18s",
                                      "&:hover": {
                                        bgcolor: "#d1fae5",
                                        borderColor: T.success,
                                        transform: "translateY(-1px)",
                                      },
                                    }}
                                  >
                                    <PrintIcon sx={{ fontSize: 12 }} />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </InfiniteScroll>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          top: "80px !important",
        }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ ...sq, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ── LR Preview Dialog ── */}
      <Dialog
        open={previewOpen}
        onClose={handlePreviewClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            ...sq,
            height: "92vh",
            border: `1.5px solid ${T.border}`,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.2)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        <Box
          sx={{
            background: "#6862e0",
            px: 2.5,
            py: 1.75,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: T.white,
              fontSize: "0.88rem",
              letterSpacing: "0.01em",
            }}
          >
            LR Receipt Preview
          </Typography>
          <IconButton
            onClick={handlePreviewClose}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.8)",
              p: 0.5,
              ...sq,
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: T.white },
            }}
          >
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>

        <DialogContent
          sx={{ p: 0, flex: 1, bgcolor: "#fff", overflow: "hidden" }}
        >
          {previewLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <CircularProgress sx={{ color: "#4f46e5" }} />
            </Box>
          ) : previewUrl ? (
            <iframe
              title="party-lr-receipt-preview"
              src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH&zoom=page-width`}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                display: "block",
                backgroundColor: "#fff",
              }}
            />
          ) : null}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            gap: 1,
            bgcolor: T.white,
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <Button
            size="small"
            onClick={handlePreviewClose}
            sx={{ ...ghostBtn, height: 32, px: 2 }}
          >
            Close
          </Button>
          <Button
            size="small"
            disabled={!previewUrl}
            onClick={() => window.open(previewUrl, "_blank")}
            startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
            sx={{ ...primaryBtn, height: 32, px: 2.5 }}
          >
            Open Full / Print
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
